/**
 * Brief 80 — MLCODashboard
 *
 * Polled (60s default) MLCO surface with five sections:
 *   1. AML status across matters (counts + red-flagged list)
 *   2. Outstanding attestations matrix
 *   3. Breach / incident log
 *   4. Control-test calendar (overdue / upcoming / complete)
 *   5. Drift indicators (Phase 3 placeholder cards)
 *
 * Plus an AI Assist panel (Brief 71.1 wiring), a manual refresh button,
 * and inline write surfaces (PolicyEditor, AttestationFlow, BreachReporter,
 * ControlTestForm) opened as modals.
 *
 * Phase 2 scope: dashboard + 4 write surfaces. No drift logic.
 */

import React, { useEffect, useState, useCallback } from "react";
import {
  RefreshCw, AlertTriangle, ShieldAlert, ClipboardCheck,
  Calendar, Activity, Sparkles, FileEdit, PenTool, AlertOctagon,
} from "lucide-react";

import PolicyEditor from "./PolicyEditor";
import AttestationFlow from "./AttestationFlow";
import BreachReporter from "./BreachReporter";
import ControlTestForm from "./ControlTestForm";
import { authFetch } from "../../config/api";

// BM-H3260 (29 Sep 2026): authFetch, not raw fetch. Raw fetch
// sends no X-Bonsai-User-Id, so this call was authenticated only when the
// browser held a bonsai_session cookie, which a password sign-in does not
// set. The /policy/* surface was moved to authFetch for exactly this
// (see config/api.js); this file was missed.
const API = process.env.REACT_APP_BACKEND_URL || "";
const POLL_INTERVAL_MS = 60_000;

const SECTION = "mb-4 rounded-xl border border-white/[0.07] bg-navy-card p-4";
const H2 = "text-sm font-semibold text-slate-100 flex items-center gap-2 mb-3";

async function fetchJson(path) {
  const r = await authFetch(`${API}${path}`, { credentials: "include" });
  if (!r.ok) throw new Error(`HTTP ${r.status} ${path}`);
  return r.json();
}

export default function MLCODashboard({ embedded = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const [openModal, setOpenModal] = useState(null);
  const [aiAssist, setAiAssist] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const j = await fetchJson("/api/policy/dashboard/all");
      setData(j);
      setLastRefreshed(new Date().toISOString());
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [load]);

  useEffect(() => {
    fetchJson("/api/policy/dashboard/ai-assist?role=mlco")
      .then((j) => setAiAssist(j))
      .catch(() => setAiAssist({ role: "mlco", guide: null }));
  }, []);

  if (loading && !data) {
    return (
      <div data-testid="mlco-dashboard-loading" className="p-6">
        <RefreshCw className="inline-block animate-spin mr-2" size={16} />
        Loading MLCO dashboard
      </div>
    );
  }
  if (error && !data) {
    return (
      <div data-testid="mlco-dashboard-error" className="p-6 text-red-700">
        Failed to load MLCO dashboard: {error}
      </div>
    );
  }
  if (!data) return null;

  return (
    <div data-testid="mlco-dashboard-page" className={embedded ? "" : "p-4 sm:p-6 max-w-screen-xl mx-auto"}>
      <Header
        embedded={embedded}
        lastRefreshed={lastRefreshed}
        onRefresh={load}
        loading={loading}
        onOpen={setOpenModal}
      />
      <AmlStatusSection aml={data.aml_status} />
      <AttestationsSection rows={data.outstanding_attestations} />
      <BreachesSection breaches={data.breaches} />
      <ControlTestsSection cts={data.control_tests} />
      <DriftSection drift={data.drift} />
      <AiAssistPanel aiAssist={aiAssist} />

      {openModal === "editor" && (
        <PolicyEditor onClose={() => { setOpenModal(null); load(); }} />
      )}
      {openModal === "attest" && (
        <AttestationFlow onClose={() => { setOpenModal(null); load(); }} />
      )}
      {openModal === "breach" && (
        <BreachReporter onClose={() => { setOpenModal(null); load(); }} />
      )}
      {openModal === "control" && (
        <ControlTestForm onClose={() => { setOpenModal(null); load(); }} />
      )}
    </div>
  );
}

// Header actions as pills. Embedded in the Compliance Dashboard tab, the
// page title is dropped (the tab already says where you are).
const PILL = "px-3 py-1.5 text-xs font-medium rounded-full ring-1 ring-white/15 cm-glass text-slate-100 cm-glass-hover disabled:opacity-50 inline-flex items-center gap-1.5";

function Header({ embedded, lastRefreshed, onRefresh, loading, onOpen }) {
  return (
    <header
      data-testid="mlco-dashboard-header"
      className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
    >
      <div>
        {!embedded && <h1 className="text-xl font-bold agents-title">MLCO Dashboard</h1>}
        <p className="text-xs text-slate-400">
          AML, attestations, breaches and control tests
          {lastRefreshed && (
            <>
              {" · "}
              <span data-testid="mlco-dashboard-last-refreshed">
                refreshed {new Date(lastRefreshed).toLocaleTimeString()}
              </span>
            </>
          )}
        </p>
      </div>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          data-testid="mlco-dashboard-refresh-btn"
          onClick={onRefresh}
          disabled={loading}
          className={PILL}
        >
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
          Refresh
        </button>
        <button
          type="button"
          data-testid="mlco-dashboard-open-editor"
          onClick={() => onOpen("editor")}
          className="px-3 py-1.5 text-xs font-semibold rounded-full text-white inline-flex items-center gap-1.5 shadow-md shadow-indigo-950/50 hover:brightness-110"
          style={{ background: "linear-gradient(90deg, #6366f1, #a855f7)" }}
        >
          <FileEdit size={14} /> Edit policy
        </button>
        <button
          type="button"
          data-testid="mlco-dashboard-open-attest"
          onClick={() => onOpen("attest")}
          className={PILL}
        >
          <PenTool size={14} /> Attest
        </button>
        <button
          type="button"
          data-testid="mlco-dashboard-open-breach"
          onClick={() => onOpen("breach")}
          className={PILL}
        >
          <AlertOctagon size={14} /> Log breach
        </button>
        <button
          type="button"
          data-testid="mlco-dashboard-open-control"
          onClick={() => onOpen("control")}
          className={PILL}
        >
          <Activity size={14} /> Control test
        </button>
      </div>
    </header>
  );
}

function AmlStatusSection({ aml }) {
  const c = aml?.counts || {};
  const reds = aml?.red_flagged_matters || [];
  return (
    <section data-testid="mlco-dashboard-section-aml" className={SECTION}>
      <h2 className={H2}>
        <ShieldAlert size={18} /> AML status across matters
      </h2>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        <Stat label="Pending admin" value={c.pending_admin || 0} testid="mlco-aml-count-pending" />
        <Stat label="Passed" value={c.passed || 0} testid="mlco-aml-count-passed" />
        <Stat label="Failed" value={c.failed || 0} testid="mlco-aml-count-failed" />
        <Stat label="Not yet required" value={c.not_yet_required || 0} testid="mlco-aml-count-not-required" />
      </div>
      <div>
        <h3 className="text-xs font-semibold text-slate-200 mb-2 flex items-center gap-1">
          <AlertTriangle size={14} className="text-amber-400" />
          Red-flagged ({reds.length}) — at pending admin for more than {aml?.red_flag_working_days} working days
        </h3>
        {reds.length === 0 ? (
          <p className="text-sm text-slate-400" data-testid="mlco-aml-red-empty">No red-flagged matters.</p>
        ) : (
          <ul data-testid="mlco-aml-red-list" className="text-sm divide-y divide-white/[0.06] border border-white/[0.07] rounded-lg">
            {reds.map((m) => (
              <li key={m._id || m.matter_reference} className="p-2 flex justify-between">
                <span>{m.matter_reference} — {m.client_name}</span>
                <span className="text-slate-400">since {m.aml_status_set_at}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

function AttestationsSection({ rows }) {
  const matrix = rows?.matrix || [];
  const summary = rows?.summary || {};
  const outstanding = matrix.filter((r) => !r.attested);
  return (
    <section data-testid="mlco-dashboard-section-attestations" className={SECTION}>
      <h2 className={H2}>
        <ClipboardCheck size={18} /> Outstanding attestations
        <span className="text-sm font-normal text-slate-400" data-testid="mlco-attest-summary">
          {summary.total_outstanding} outstanding across {summary.total_staff} staff
        </span>
      </h2>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-[11px] uppercase tracking-wider text-slate-400">
            <tr>
              <th className="p-2">Staff</th>
              <th className="p-2">Role</th>
              <th className="p-2">Policy</th>
              <th className="p-2">Version</th>
              <th className="p-2">Status</th>
            </tr>
          </thead>
          <tbody data-testid="mlco-attest-matrix">
            {outstanding.length === 0 ? (
              <tr><td colSpan={5} className="p-3 text-slate-400">All staff up to date</td></tr>
            ) : outstanding.map((r) => (
              <tr key={`${r.staff_email}|${r.policy_slug}`} className="border-t border-white/[0.06]">
                <td className="p-2">{r.staff_name || r.staff_email}</td>
                <td className="p-2 text-slate-400">{r.staff_role}</td>
                <td className="p-2">{r.policy_slug}</td>
                <td className="p-2">{r.policy_version_label}</td>
                <td className="p-2">
                  <span className="text-xs px-2 py-0.5 rounded bg-amber-500/15 text-amber-300 ring-1 ring-amber-500/30">outstanding</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function BreachesSection({ breaches }) {
  const list = breaches?.breaches || [];
  return (
    <section data-testid="mlco-dashboard-section-breaches" className={SECTION}>
      <h2 className={H2}>
        <AlertOctagon size={18} /> Breach and incident log
        <span className="text-sm font-normal text-slate-400" data-testid="mlco-breach-open-count">
          {breaches?.open_count || 0} open
        </span>
      </h2>
      {list.length === 0 ? (
        <p className="text-sm text-slate-400" data-testid="mlco-breach-empty">No breaches logged.</p>
      ) : (
        <ul className="divide-y divide-white/[0.06] border border-white/[0.07] rounded-lg" data-testid="mlco-breach-list">
          {list.map((b) => (
            <li key={b._id} className="p-3 text-sm">
              <div className="flex items-center justify-between mb-1">
                <span className="font-semibold text-slate-100">{b.title}</span>
                <span className={
                  "text-xs px-2 py-0.5 rounded " +
                  (b.status === "open"
                    ? "bg-red-500/15 text-red-300 ring-1 ring-red-500/30"
                    : b.status === "investigating"
                    ? "bg-amber-500/15 text-amber-300 ring-1 ring-amber-500/30"
                    : "cm-glass text-slate-300")
                }>{b.status}</span>
              </div>
              <div className="text-xs text-slate-400">
                {b.policy_slug} · severity {b.severity} · occurred {b.occurred_at}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function ControlTestsSection({ cts }) {
  const c = cts?.counts || {};
  return (
    <section data-testid="mlco-dashboard-section-control-tests" className={SECTION}>
      <h2 className={H2}>
        <Calendar size={18} /> Control-test calendar
      </h2>
      <div className="grid grid-cols-3 gap-3 mb-3">
        <Stat label="Overdue" value={c.overdue || 0} testid="mlco-ct-count-overdue" />
        <Stat label="Upcoming" value={c.upcoming || 0} testid="mlco-ct-count-upcoming" />
        <Stat label="Complete" value={c.complete_in_window || 0} testid="mlco-ct-count-complete" />
      </div>
      <CTGroup title="Overdue" rows={cts?.overdue || []} testid="mlco-ct-overdue" />
      <CTGroup title="Upcoming" rows={cts?.upcoming || []} testid="mlco-ct-upcoming" />
    </section>
  );
}

function CTGroup({ title, rows, testid }) {
  if (rows.length === 0) return null;
  return (
    <div className="mb-3">
      <h3 className="text-xs font-semibold text-slate-200 mb-1">{title}</h3>
      <ul data-testid={testid} className="text-sm divide-y divide-white/[0.06] border border-white/[0.07] rounded-lg">
        {rows.map((t) => (
          <li key={t._id || t.test_ref} className="p-2 flex justify-between">
            <span>{t.test_ref} — {t.title}</span>
            <span className="text-slate-400">due {t.due_by}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function DriftSection({ drift }) {
  const cards = drift?.cards || [];
  // Phase 3 placeholder: say nothing until there is something to show.
  if (cards.length === 0) return null;
  return (
    <section data-testid="mlco-dashboard-section-drift" className={SECTION}>
      <h2 className={H2}>
        <Activity size={18} /> Drift indicators
        <span className="text-xs px-2 py-0.5 rounded cm-glass text-slate-300">Phase 3 will fill</span>
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {cards.map((c) => (
          <div key={c.policy_slug} data-testid={`mlco-drift-card-${c.policy_slug}`} className="rounded-lg border border-white/[0.07] bg-slate-900/40 p-3">
            <div className="font-semibold text-slate-100">{c.policy_slug}</div>
            <div className="text-sm text-slate-400">{c.headline}</div>
            <div className="text-xs text-slate-400 mt-1">{c.description}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

function AiAssistPanel({ aiAssist }) {
  // No guide loaded: hide the panel rather than show an empty box.
  if (!aiAssist || !aiAssist.guide) return null;
  return (
    <section data-testid="mlco-dashboard-section-ai-assist" className={SECTION}>
      <h2 className={H2}>
        <Sparkles size={18} /> AI Assist
        <span className="text-xs text-slate-400">role-aware context (Brief 71.1)</span>
      </h2>
      {!aiAssist || !aiAssist.guide ? (
        <p className="text-sm text-slate-400" data-testid="mlco-ai-empty">
          {aiAssist?.warning || "No staff guide loaded."}
        </p>
      ) : (
        <div data-testid="mlco-ai-guide">
          <div className="font-semibold text-slate-100">{aiAssist.guide.title}</div>
          <div className="text-sm whitespace-pre-wrap text-slate-300 mt-1">
            {aiAssist.guide.body}
          </div>
          <div className="text-xs text-slate-400 mt-2">version {aiAssist.guide.version}</div>
        </div>
      )}
    </section>
  );
}

// Count tile with a hue stripe (never green, amber or red: status colours).
const STAT_HUES = [
  "linear-gradient(90deg, #6366f1, #a855f7)",
  "linear-gradient(90deg, #8b5cf6, #d946ef)",
  "linear-gradient(90deg, #3b82f6, #6366f1)",
  "linear-gradient(90deg, #06b6d4, #3b82f6)",
];
function Stat({ label, value, testid }) {
  const hue = STAT_HUES[label.length % STAT_HUES.length];
  return (
    <div data-testid={testid} className="relative overflow-hidden rounded-lg border border-white/[0.07] bg-slate-900/40 px-3 py-2">
      <div aria-hidden className="absolute inset-x-0 top-0 h-0.5" style={{ background: hue }} />
      <div className="text-[11px] text-slate-400">{label}</div>
      <div className="text-xl font-bold text-white tabular-nums">{value}</div>
    </div>
  );
}
